﻿import { getMomentTz } from "@dexteel/mesf-core";
import {
	Add,
	CloseOutlined,
	MoveDown,
	MoveUp,
	RemoveRedEye,
	South,
	Update,
} from "@mui/icons-material";
import {
	alpha,
	Box,
	Checkbox,
	IconButton,
	Theme,
	Tooltip,
	Typography,
} from "@mui/material";
import { EventToCompare } from "../../../models/Event";
import {
	getEventBorderColor,
	getGridTemplateColumns,
	getGroupColor,
	gridRowSx,
} from "./confirmDraftEventsUtils";

const moment = getMomentTz();

const NEW_FIELD_MAP = {
	ScheduledChargedTons: "NewScheduledChargedTons",
	ScheduledRolledTons: "NewScheduledRolledTons",
	ScheduledFinishedTons: "NewScheduledFinishedTons",
	Duration: "NewDuration",
	SectionId: "NewSectionId",
	StatusCode: "NewStatusCode",
	BilletsStatusCode: "NewBilletsStatusCode",
	InquiryStatusCode: "NewInquiryStatusCode",
	BudgetTPH: "NewBudgetTPH",
	ScheduledRollingHours: "NewScheduledRollingHours",
	ScheduledMillYield: "NewScheduledMillYield",
	ScheduledFinishedYield: "NewScheduledFinishedYield",
	Comments: "NewComments",
} as const;

const ORIGINAL_FIELD_MAP = {
	ScheduledChargedTons: "OriginalScheduledChargedTons",
	ScheduledRolledTons: "OriginalScheduledRolledTons",
	ScheduledFinishedTons: "OriginalScheduledFinishedTons",
	Duration: "OriginalDuration",
	SectionId: "OriginalSectionId",
	StatusCode: "OriginalStatusCode",
	BilletsStatusCode: "OriginalBilletsStatusCode",
	InquiryStatusCode: "OriginalInquiryStatusCode",
	BudgetTPH: "OriginalBudgetTPH",
	ScheduledRollingHours: "OriginalScheduledRollingHours",
	ScheduledMillYield: "OriginalScheduledMillYield",
	ScheduledFinishedYield: "OriginalScheduledFinishedYield",
	Comments: "OriginalComments",
} as const;

type EventToCompareWithNews = EventToCompare & Record<string, any>;

type ActionTypeConfig = {
	code: "C" | "U" | "D" | "M";
	name: string;
	label: string;
	color: "success" | "warning" | "error" | "action";
	icon?: React.ElementType;
	getIcon?: (event: EventToCompare) => React.ElementType | null;
};

const actionTypeCodes: ActionTypeConfig[] = [
	{
		code: "C",
		name: "Created",
		label: "NEW",
		color: "success",
		icon: Add,
	},
	{
		code: "U",
		name: "Updated",
		label: "UPD",
		color: "warning",
		icon: Update,
	},
	{
		code: "D",
		name: "Deleted",
		label: "DEL",
		color: "error",
		icon: CloseOutlined,
	},
	{
		code: "M",
		name: "Moved",
		label: "MOV",
		color: "action",
		getIcon: (event: EventToCompare) =>
			event.PositionDelta > 0 ? MoveDown : MoveUp,
	},
];

const getRowBgColor =
	({
		isHovered,
		selected,
		actionTypeCode,
	}: {
		isHovered: boolean;
		selected: boolean;
		actionTypeCode?: "C" | "U" | "D" | "M" | null;
	}) =>
	(theme: Theme) => {
		if (isHovered) {
			return alpha(theme.palette.action.hover, 0.1);
		}

		if (selected) {
			return alpha("#2196F3", 0.1);
		}

		if (actionTypeCode === "D") {
			return alpha(theme.palette.error.main, 0.1);
		}

		if (actionTypeCode === "C") {
			return alpha(theme.palette.success.main, 0.1);
		}

		return "transparent";
	};

const ChangedIndicator = ({ show }: { show: boolean }) =>
	show ? (
		<Box
			component="span"
			sx={{
				width: 4,
				height: 4,
				borderRadius: "50%",
				backgroundColor: "warning.main",
				display: "inline-block",
				ml: 0.5,
				mb: 0.15,
				flexShrink: 0,
			}}
		/>
	) : null;

export const EventRow = ({
	event,
	showAction = false,
	showChangedFields = false,
	selected = false,
	onSelect,
	isHovered = false,
	onHoverChange,
	showBeforeAfter = true,
	openDetails,
}: {
	event: EventToCompare;
	showAction?: boolean;
	showChangedFields?: boolean;
	selected?: boolean;
	onSelect?: () => void;
	isHovered?: boolean;
	onHoverChange?: (hovered: boolean) => void;
	showBeforeAfter?: boolean;
	openDetails?: () => void;
}) => {
	const borderColor = getEventBorderColor(event);

	const isProduction = event.EventTypeCode === "P";
	const duration = event.Duration
		? moment.duration(event.Duration, "minutes").format("d[d] h[h] m[m]")
		: "";

	const newDuration =
		"NewDuration" in event && event.NewDuration
			? moment.duration(event.NewDuration, "minutes").format("d[d] h[h] m[m]")
			: null;

	const originalDuration =
		"OriginalDuration" in event && event.OriginalDuration
			? moment
					.duration(event.OriginalDuration, "minutes")
					.format("d[d] h[h] m[m]")
			: null;

	const e = event as EventToCompareWithNews;

	const actionType = actionTypeCodes.find(
		(a) => a.code === event.ActionTypeCode,
	);

	const values = isProduction
		? [
				{
					field: "ScheduledChargedTons",
					label: "Chd",
					value: event.ScheduledChargedTons,
				},
				{
					field: "ScheduledRolledTons",
					label: "Rld",
					value: event.ScheduledRolledTons,
				},
				{
					field: "ScheduledFinishedTons",
					label: "Fnd",
					value: event.ScheduledFinishedTons,
				},
			]
		: [{ field: "", label: "", value: event.EventTypeName }];

	const hasChanges = event.ChangedFields && event.ChangedFields.length > 0;
	const changedFieldsList =
		hasChanges && event.ChangedFields
			? event.ChangedFields.split(",").map((f) => f.trim())
			: [];

	const formatFieldValue = (field: string, value: any): string => {
		if (value === null || value === undefined) return "-";

		if (field === "Duration") {
			return typeof value === "number"
				? moment.duration(value, "minutes").format("d[d] h[h] m[m]")
				: value;
		}

		if (field === "SectionId") {
			return event.SectionName || value?.toString() || "-";
		}

		if (typeof value === "number") {
			if (
				field.includes("Yield") ||
				field.includes("TPH") ||
				field.includes("Hours")
			) {
				return value.toFixed(2);
			}
			return value.toFixed(0);
		}

		return value.toString();
	};

	const buildTooltipContent = () => {
		if (!hasChanges) return "";
		if (changedFieldsList.length === 0) return "";

		return (
			<Box
				sx={{
					display: "table",
					borderSpacing: "0 4px",
					minWidth: 200,
				}}
			>
				<Box
					sx={{ display: "table-caption", captionSide: "top", marginBottom: 1 }}
				>
					<Typography variant="subtitle2" fontWeight="bold">
						Changes
					</Typography>
				</Box>

				{changedFieldsList.map((field) => {
					const newField = NEW_FIELD_MAP[field as keyof typeof NEW_FIELD_MAP];
					const newValue =
						newField && e[newField] !== undefined ? e[newField] : null;

					const originalField =
						ORIGINAL_FIELD_MAP[field as keyof typeof ORIGINAL_FIELD_MAP];
					const originalValue =
						originalField && e[originalField] !== undefined
							? e[originalField]
							: null;

					const currentValue = (event as any)[field];

					const hasNewValue = newValue != null;
					const hasOriginalValue = originalValue != null;

					if (!hasNewValue && !hasOriginalValue) return null;

					return (
						<Box
							key={field}
							sx={{
								display: "table-row",
							}}
						>
							<Box
								component="span"
								sx={{
									display: "table-cell",
									textAlign: "left",
									fontWeight: "bold",
									whiteSpace: "nowrap",
								}}
							>
								<Typography variant="caption">{field}:</Typography>
							</Box>
							<Box
								component="span"
								sx={{
									display: "table-cell",
									textAlign: "right",
									whiteSpace: "nowrap",
								}}
							>
								<Typography variant="caption">
									{hasOriginalValue && (
										<>
											<Box
												component="span"
												sx={{ textDecoration: "line-through" }}
											>
												{formatFieldValue(field, originalValue)}
											</Box>
											<Box component="span"> → </Box>
										</>
									)}
									<Box
										component="span"
										sx={{
											textDecoration: hasNewValue ? "line-through" : "none",
										}}
									>
										{formatFieldValue(field, currentValue)}
									</Box>
									{hasNewValue && (
										<>
											<Box component="span"> → </Box>
											<Box component="span">
												{formatFieldValue(field, newValue)}
											</Box>
										</>
									)}
								</Typography>
							</Box>
						</Box>
					);
				})}
			</Box>
		);
	};

	const tooltipContent = buildTooltipContent();
	const Icon = actionType?.getIcon?.(event) ?? actionType?.icon;

	return (
		<Tooltip
			title={tooltipContent}
			enterDelay={800}
			arrow
			placement="top"
			disableInteractive
		>
			<Box
				sx={{
					...gridRowSx,
					gridTemplateColumns: getGridTemplateColumns({
						showAction,
						showChangedFields,
					}),
					py: 0.75,
					borderLeft: `4px solid ${borderColor}`,
					borderBottom: "1px solid",
					borderBottomColor: "divider",
					minHeight: 40,
					backgroundColor: getRowBgColor({
						isHovered,
						selected,
						actionTypeCode: event.ActionTypeCode,
					}),
					transition: "background-color 0.15s ease-in-out",
					"&:hover": {
						backgroundColor: (theme) => alpha(theme.palette.action.hover, 0.1),
					},
				}}
				onPointerEnter={() => onHoverChange?.(true)}
				onPointerLeave={() => onHoverChange?.(false)}
				data-test-id={`event_${event.EventId}`}
			>
				{onSelect ? (
					<Box sx={{ display: "flex", alignItems: "center" }}>
						<Checkbox
							color="primary"
							checked={selected}
							onChange={onSelect}
							sx={{ p: 0 }}
						/>
					</Box>
				) : null}
				{showAction && (
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							justifyContent: "flex-start",
							width: "100%",
							gap: 0.5,
						}}
					>
						{Icon && <Icon color={actionType?.color} fontSize="small" />}
						{event.ActionTypeCode && (
							<Typography
								variant="caption"
								color={
									actionType?.color as
										| "error"
										| "warning"
										| "success"
										| "action"
								}
								sx={{
									fontWeight: "bold",
									marginTop: 0.25,
								}}
							>
								{actionType?.label}
							</Typography>
						)}
					</Box>
				)}
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						backgroundColor:
							event.GroupId !== null
								? alpha(getGroupColor(event.GroupId), 0.2)
								: "action.hover",
						borderRadius: "4px",
						px: 1,
						py: 0.25,
					}}
				>
					<Typography
						variant="body2"
						fontWeight={600}
						noWrap
						sx={{
							textDecoration:
								event.ActionTypeCode === "D" ? "line-through" : "none",
						}}
					>
						{event.SectionName || event.EventTypeName || "-"}
					</Typography>
				</Box>
				<Box flexDirection="column">
					{values.map(({ field, label, value }, idx) => {
						const numericValue = typeof value === "number";
						const isChanged = event.ChangedFields?.includes(field);

						const newField = NEW_FIELD_MAP[field as keyof typeof NEW_FIELD_MAP];
						const newValue =
							newField && e[newField] !== undefined ? e[newField] : null;

						const originalField =
							ORIGINAL_FIELD_MAP[field as keyof typeof ORIGINAL_FIELD_MAP];
						const originalValue =
							originalField && e[originalField] !== undefined
								? e[originalField]
								: null;

						const hasNewValue = newValue != null;
						const hasOriginalValue = originalValue != null;

						return (
							<Box
								key={idx}
								sx={{
									display: "flex",
									alignItems: "center",
									justifyContent: "space-between",
									gap: 1,
									color: "grey",
									fontSize: "0.65rem",
								}}
							>
								{label != null ? (
									<Box>
										<Box component="span">{label}</Box>
										<ChangedIndicator show={!!isChanged} />
									</Box>
								) : null}

								<Box
									sx={{
										display: "block",
										alignItems: "center",
										gap: 1,
										whiteSpace: "nowrap",
									}}
								>
									{showBeforeAfter ? (
										<>
											{hasOriginalValue && (
												<>
													<Box
														component="span"
														sx={{
															fontSize: numericValue ? "0.65rem" : undefined,
														}}
														color="text.disabled"
													>
														{originalValue?.toFixed(0)}
													</Box>
													<Box component="span"> → </Box>
												</>
											)}

											<Box
												component="span"
												sx={{
													fontSize: numericValue ? "0.65rem" : undefined,
												}}
												color={hasNewValue ? "text.disabled" : "text.secondary"}
											>
												{numericValue ? value.toFixed(0) : value}
											</Box>

											{hasNewValue && (
												<>
													<Box component="span"> → </Box>
													<Box component="span">{newValue}</Box>
												</>
											)}
										</>
									) : (
										<>
											<Box
												component="span"
												sx={{
													fontSize: numericValue ? "0.65rem" : undefined,
												}}
												color="text.secondary"
											>
												{numericValue ? value.toFixed(0) : value}
											</Box>
										</>
									)}
								</Box>
							</Box>
						);
					})}
				</Box>
				<Typography variant="caption" color="text.secondary">
					{moment(event.Start).format("DD/MM HH:mm")}
				</Typography>
				<Typography variant="caption" color="text.secondary">
					{event.End ? moment(event.End).format("DD/MM HH:mm") : "-"}
				</Typography>
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
						gap: 1,
					}}
				>
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							gap: 0,
							whiteSpace: "nowrap",
							flexDirection: "column",
						}}
					>
						{showBeforeAfter ? (
							<>
								{originalDuration && (
									<>
										<Box display="flex" alignItems="center">
											<Typography variant="caption" color="text.disabled">
												{originalDuration}
											</Typography>
										</Box>
										<South
											sx={{
												fontSize: 12,
												color: "text.disabled",
											}}
										/>
									</>
								)}

								<Box display="flex" alignItems="center">
									<Typography
										variant="caption"
										color={newDuration ? "text.disabled" : "text.secondary"}
									>
										{duration}
									</Typography>
									{(newDuration || originalDuration) && (
										<ChangedIndicator
											show={!!event.ChangedFields?.includes("Duration")}
										/>
									)}
								</Box>

								{newDuration && (
									<>
										<South
											sx={{
												fontSize: 12,
												color: "text.disabled",
											}}
										/>
										<Typography variant="caption" color="text.secondary">
											{newDuration}
										</Typography>
									</>
								)}
							</>
						) : (
							<>
								<Box display="flex" alignItems="center">
									<Typography variant="caption" color="text.secondary">
										{duration}
									</Typography>
									{(newDuration || originalDuration) && (
										<ChangedIndicator
											show={!!event.ChangedFields?.includes("Duration")}
										/>
									)}
								</Box>
							</>
						)}
					</Box>
				</Box>
				{showChangedFields ? (
					<Tooltip title={event.ChangedFields}>
						<Typography
							variant="caption"
							color="text.secondary"
							overflow="hidden"
							textOverflow="ellipsis"
						>
							{event.ChangedFields}
						</Typography>
					</Tooltip>
				) : null}
				{showChangedFields && showAction ? (
					<IconButton size="small" title="See event data" onClick={openDetails}>
						<RemoveRedEye fontSize="small" />
					</IconButton>
				) : null}
			</Box>
		</Tooltip>
	);
};
